import * as React from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import type { SidebarNavProps } from './types';
import { getNavItems } from './utils';
import { NavItem } from './NavItem';
import { Button } from '@/components/ui/Button';
import { useEchoStore } from '@/context/EchoContext';
import { useUiStore } from '@/store/uiStore';

/**
 * Separator Component
 * Renders a horizontal divider line with responsive width adjustments.
 */
const Separator: React.FC<{ className?: string; isCollapsed: boolean }> = ({
  className,
  isCollapsed,
}) => (
  <div
    className={clsx(
      'mx-auto h-px bg-gray-300',
      !isCollapsed ? 'w-full xl:w-187' : 'w-[48px]',
      className
    )}
  />
);

/**
 * SidebarNav Component
 *
 * Renders the main navigation menu within the sidebar.
 * Handles "Create Echo" / "My Echo" logic based on user creator status.
 */
export const SidebarNav: React.FC<SidebarNavProps> = ({ isCollapsed, onItemClick }) => {
  const items = React.useMemo(() => getNavItems(isCollapsed), [isCollapsed]);
  const { myEchoes } = useEchoStore();
  const openModal = useUiStore((state) => state.openModal);
  const location = useLocation();

  // Logic: User is a creator if they have at least one approved/created echo
  const isCreator = myEchoes.length > 0;
  const isCreateEchoActive = location.pathname.startsWith('/create-echo');

  const handleMainAction = () => {
    openModal('creator');
  };

  const handleItemClick = (_e: React.MouseEvent<HTMLAnchorElement>, _to: string) => {
    if (onItemClick) onItemClick();
  };

  // Items: 0: Home, 1: Account, 2: Chats, 3: My Echo
  const groupOne = items.slice(0, 2);
  const groupTwo = items.slice(2);

  // Variant selection logic for sidebar action button
  const buttonVariant = isCreateEchoActive && !isCreator ? 'sidebar-active' : 'sidebar-default';

  return (
    <div
      className={clsx('w-full', !isCollapsed && 'py-[clamp(6px,1dvh,16px)] pl-4 pr-[42px] xl:p-0')}
    >
      <nav
        className={clsx(
          'flex flex-col',
          isCollapsed ? 'gap-1.5' : 'gap-[clamp(2px,0.4dvh,8px)] xl:gap-2'
        )}
      >
        {/* Group 1 */}
        {groupOne.map((item) => (
          <NavItem
            key={item.to}
            isCollapsed={isCollapsed}
            {...item}
            onClick={(e) => handleItemClick(e, item.to)}
          />
        ))}

        <Separator className='my-0.5' isCollapsed={isCollapsed} />

        {/* Group 2 */}
        {groupTwo.map((item) => (
          <NavItem
            key={item.to}
            isCollapsed={isCollapsed}
            {...item}
            onClick={(e) => handleItemClick(e, item.to)}
          />
        ))}
      </nav>

      {/* Conditionally render the Create Echo section only if the user is NOT a creator */}
      {!isCreator && (
        <>
          <Separator
            className={clsx(
              isCollapsed ? 'my-3 short:my-2' : 'my-[clamp(4px,0.8dvh,16px)] xl:my-4'
            )}
            isCollapsed={isCollapsed}
          />

          {/* Main Action Area */}
          <div className={clsx('flex w-full justify-center', isCollapsed && 'mt-0')}>
            {/* Scenario 1: Desktop Collapsed */}
            {isCollapsed && (
              <Button
                onClick={handleMainAction}
                variant={buttonVariant}
                className={clsx(
                  'h-[70px] w-[90px]',
                  'rounded-12',
                  'flex-col'
                  // Font styles handled by Button variant
                )}
              >
                <span>Create</span>
                <span>Echo</span>
              </Button>
            )}

            {/* Scenario 2: Not Collapsed */}
            {!isCollapsed && (
              <Button
                onClick={handleMainAction}
                variant={buttonVariant}
                className={clsx('w-[186px]', 'h-[49px]', 'rounded-30', 'flex-row')}
              >
                Create Echo
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SidebarNav;
